import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaCa010 } from '~/endpoints/ApiTaCa010'
import { ApiTaCa030 } from '~/endpoints/ApiTaCa030'
import { ApiTaCm020 } from '~/endpoints/ApiTaCm020'
import { CurriculumType } from '~/store/apis/TopContent'

// State types
export interface ServerTimes {
  serverDate: string
  serverTime: string
  serverDateAndTime: string
}
export const getEmptyServerTimes = (): ServerTimes => ({
  serverDate: '',
  serverTime: '',
  serverDateAndTime: '',
})

interface CalendarInfo {
  schedules: {
    memberScheduleId: number
    lessonDate: string
    isCompleted: boolean
    subjectId: CurriculumType
    subjectsName: string
  }[]
}
const getEmptyCalendarInfo = (): CalendarInfo => ({
  schedules: [],
})

// State action parameter
interface ActionParamCalendarInfo {
  deliveryYear: string
  deliveryMonth: string
  courseId: string
  planId: string
}

interface ActionMemberSchedule {
  memberScheduleId: number
  lessonDate: string
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Calendar',
})
export default class Calendar extends VuexModule {
  // State
  private Calendar_ = getEmptyCalendarInfo()

  private ServerTimes_ = getEmptyServerTimes()

  // Getter: calendar
  get calendar(): CalendarInfo {
    return this.Calendar_
  }

  get serverTimes(): ServerTimes {
    return this.ServerTimes_
  }

  @Mutation
  updateCalendar(values: CalendarInfo) {
    this.Calendar_ = values
  }

  @Mutation
  updateServerTimes(values: ServerTimes) {
    this.ServerTimes_ = values
  }

  @Action
  clear() {
    this.context.commit('updateCalendar', getEmptyCalendarInfo())
    this.context.commit('updateServerTimes', getEmptyServerTimes())
  }

  @Action
  async getCalendarInfo(param: ActionParamCalendarInfo) {
    logger.trace('in: getCalendarInfo')
    const api = new ApiTaCa010.Api()

    // API: ApiTaCa010
    const result = await api.get<CalendarInfo | null>(
      {
        query: {
          deliveryYear: param.deliveryYear,
          deliveryMonth: param.deliveryMonth,
          courseId: param.courseId,
          planId: param.planId
        },
      },
      // success
      (data, _) => {
        return {
          schedules: data.schedules.map(elem => {
            return {
              memberScheduleId: elem.memberScheduleId,
              lessonDate: elem.lessonDate,
              isCompleted: elem.isCompleted,
              subjectId: elem.subjectId as CurriculumType,
              subjectsName: elem.subjectsName,
            }
          }),
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateCalendar', result)
  }

  @Action
  async updateMemberSchedule(param: ActionMemberSchedule) {
    logger.trace('in: updateMemberSchedule')
    const api = new ApiTaCa030.Api()

    // API: ApiTaCa030
    const result = await api.post<boolean | null>(
      {
        body: {
          memberScheduleId: param.memberScheduleId,
          lessonDate: param.lessonDate,
        },
      },
      // success
      (_, __) => {
        return true
      },
      // Error
      (_, __) => {
        return false
      }
    )

    return result
  }

  @Action
  async getServerTimes() {
    logger.trace('in: getServerTimes')
    const api = new ApiTaCm020.Api()

    // API: ApiTaCm020
    const result = await api.get<ServerTimes | null>(
      {},
      // success
      (data, _) => {
        return {
          serverDate: data.serverDate,
          serverTime: data.serverTime,
          serverDateAndTime: data.serverDateAndTime,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateServerTimes', result)
  }
}
